/**
 * Confirmation dialog and toasts.
 *
 * §13 requires an explicit yes before the original is replaced, and §34 wants
 * messages that come with something to do about them. Both are quiet: a modal
 * that appears for a routine save teaches people to dismiss modals without
 * reading, which is exactly what must not happen the one time it matters.
 *
 * The dialog traps focus and returns it, and Escape always means "no" —
 * a destructive action must never be reachable by pressing Enter twice (§26).
 */
import { useEffect, useRef } from 'react';
import { Icon } from '@moduviewer/design/icon';
import type { PendingConfirm, Toast } from '../lib/session.js';

export function ConfirmDialog({ request }: { request: PendingConfirm }): React.JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null;
    // Focus lands on Cancel, not Confirm: the safe option is the default.
    cancel.current?.focus();
    return () => restoreTo.current?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); request.resolve(false); return; }
      if (e.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [request]);

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) request.resolve(false); }}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-body"
        ref={panel}
      >
        <div className="dialog__head">
          <span className={`dialog__mark${request.danger ? ' dialog__mark--danger' : ''}`}>
            <Icon name={request.danger ? 'alert' : 'info'} size={18} />
          </span>
          <h2 id="dialog-title" className="dialog__title">{request.title}</h2>
        </div>

        <p id="dialog-body" className="dialog__body">{request.body}</p>

        {request.items && request.items.length > 0 ? (
          <ul className="dialog__items">
            {request.items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        ) : null}

        <div className="dialog__actions">
          <button ref={cancel} className="btn btn--outline" onClick={() => request.resolve(false)}>
            취소
          </button>
          <button
            className={`btn ${request.danger ? 'btn--danger btn--outline' : 'btn--primary'}`}
            onClick={() => request.resolve(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Toasts(
  { toasts, messages, onDismiss }: {
    toasts: readonly Toast[];
    messages: Record<string, string>;
    onDismiss: (id: number) => void;
  },
): React.JSX.Element {
  return (
    // Announced politely: a save confirmation must not interrupt what a screen
    // reader is already saying (§26).
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.tone}`}>
          <Icon
            name={toast.tone === 'info' ? 'check' : 'alert'}
            size={15}
            className="toast__icon"
          />
          <div className="toast__body">
            <div>{messages[toast.messageKey] ?? toast.messageKey}</div>
            {toast.detail ? <div className="toast__detail">{toast.detail}</div> : null}
          </div>
          <button
            className="btn btn--icon"
            aria-label="닫기"
            onClick={() => onDismiss(toast.id)}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
